import { flashMessageSlice } from '_services/Redux/FlashMessage/FlashMessageSlice';
import type { AxiosResponse } from 'axios';

type BaseQueryType = Promise<AxiosResponse>;

type ResolveType = (value: AxiosResponse | PromiseLike<AxiosResponse>) => void;

class RequestLimitter {
    MAX_CALLS: number;
    processing: boolean;
    DURATION: number;
    queue: {
        requestId: number;
        promise: () => BaseQueryType;
        resolve: ResolveType;
        reject: (value: unknown) => void;
        enqueuedAt: number;
    }[];
    itemsTime: number[];
    lastTimeoutFunction: ReturnType<typeof setTimeout> | undefined;

    constructor() {
        this.queue = [];
        this.itemsTime = [];
        this.MAX_CALLS = 100; // Max calls need to place in below time
        this.DURATION = 1 * 60 * 1000; // 1 minute
        this.processing = false;
        this.lastTimeoutFunction = undefined;
    }

    pushRequest = (promise, requestId): BaseQueryType => {
        return new Promise((resolve, reject) => {
            const now = Date.now();
            // Add the promise to the queue.
            this.queue.push({
                requestId,
                promise,
                resolve,
                reject,
                enqueuedAt: now,
            });
            this.itemsTime.push(now);

            // If the queue is not being processed, we process it.
            if (!this.processing) {
                this.processing = true;
                this.processQueue();
            }
        });
    };

    processQueue = async () => {
        try {
            const aMinBefore = Date.now() - this.DURATION;
            const { sayError } = flashMessageSlice.actions;

            this.itemsTime = this.itemsTime.filter(
                (requestId) => requestId > aMinBefore,
            );

            if (this.queue.length > 0) {
                this.processing = true;
                if (this.itemsTime.length < this.MAX_CALLS) {
                    const item = this.queue.shift();
                    const data = item?.promise();

                    if (data) {
                        item?.resolve(data);
                    }
                    this.processQueue();
                    return;
                } else {
                    // when too many request placed
                    // Use dynamic import to avoid circular dependency
                    import('_services/Redux/store').then(({ dispatch }) => {
                        dispatch(
                            sayError(
                                'There has been too many requests. Please wait a minute before continuing',
                            ),
                        );
                    });
                }

                this.lastTimeoutFunction = setTimeout(() => {
                    this.processQueue();
                }, this.itemsTime[0] - aMinBefore);
            } else {
                this.processing = false;
            }
        } catch (e) {
            const item = this.queue.shift();
            item?.reject(e);
        }
    };

    removeRequests = (reason = 'queue-flush', route?: string) => {
        // We delete the promise from the queue using the given id.

        clearInterval(this.lastTimeoutFunction);
        this.processing = false;

        const queueSize = this.queue.length;
        const now = Date.now();
        const oldestEnqueued = this.queue.reduce(
            (min, item) => Math.min(min, item.enqueuedAt ?? now),
            now,
        );
        const oldestAgeMs = now - oldestEnqueued;

        for (let index = 0; index < this.queue.length; index++) {
            const queuedItem = this.queue[index];
            const handledCancellation = {
                code: 'QUEUE_FLUSHED',
                message: 'Requests cancelled because user left the page',
                response: {
                    status: 0,
                    message: 'Requests cancelled because user left the page',
                },
                reason,
                route,
                queueSize,
                oldestAgeMs,
            };
            queuedItem?.reject(handledCancellation);
        }
        this.queue = [];
    };
}

const instance = new RequestLimitter();
Object.freeze(RequestLimitter);

export default instance;
